// src/pages/TaskPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { connect } from "react-redux";
import styled, { createGlobalStyle, keyframes, css } from "styled-components";
import { format, formatDistanceToNow } from "date-fns";
import { parseDate } from "../utils/helpers/date";
import {
  User,
  DollarSign,
  Calendar,
  MapPin,
  Briefcase,
  Image as ImageIcon,
  Clock,
  Settings,
  Star,
  MessageCircle,
  ExternalLink,
  Flag,
  Pencil,
  Check,
  Link2,
  PlusCircle,
  ChevronRight,
} from "lucide-react";

import NavBar from "../components/layout/NavBar";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import VerificationBadge from "../components/common/VerificationBadge";
import UserRating from "../components/common/UserRating";
import AvatarComponent from "../components/Avatar";

// Utility function to normalize URL (add https:// if missing)
const normalizeURL = (string) => {
  if (!string) return null;
  let url = string.trim();
  if (!url) return null;
  // Add https:// if no protocol
  if (!/^https?:\/\//i.test(url)) {
    url = 'https://' + url;
  }
  return url;
};

// Utility function to validate if a string is a URL
const isValidURL = (string) => {
  try {
    const normalized = normalizeURL(string);
    if (!normalized) return false;
    const url = new URL(normalized);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
};

// Utility function to get domain name from URL
const getDomainName = (url) => {
  try {
    const normalized = normalizeURL(url);
    if (!normalized) return url;
    const domain = new URL(normalized).hostname;
    return domain.replace('www.', '');
  } catch (_) {
    return url?.trim() || url;
  }
};

// Utility function to linkify URLs in text
const linkifyText = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (!part) return null;
    if (part.match(/^https?:\/\//) || part.match(/^www\./)) {
      const href = part.startsWith('www.') ? `https://${part}` : part;
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'hsl(217, 91%, 60%)',
            textDecoration: 'none',
            wordBreak: 'break-all'
          }}
          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

/* =============== DESIGN TOKENS (matching BrowseTasks) =============== */
const theme = {
  colors: {
    primary: "hsl(217, 91%, 60%)",
    primaryHover: "hsl(217, 91%, 50%)",
    primaryLight: "hsl(217, 91%, 96%)",
    primaryText: "hsl(217, 91%, 50%)",
    secondary: "#6366f1",
    textHeading: "#0f172a",
    textPrimary: "#0f172a",
    textSecondary: "#334155",
    textBody: "#334155",
    textMuted: "#64748b",
    surface: "#ffffff",
    surfaceHover: "#f8fafc",
    background: "#f8fafc",
    border: "#e2e8f0",
    borderLight: "#f1f5f9",
    success: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
  },
  gradients: {
    primary: "none",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.04)",
    md: "0 4px 12px rgba(15, 23, 42, 0.06)",
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    xxl: "32px",
  },
  borderRadius: {
    sm: "0.375rem",
    md: "0.875rem",
    lg: "0.875rem",
    xl: "0.875rem",
  },
};

/* =============== GLOBAL/ANIM =============== */
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideInUp = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

const GlobalStyle = createGlobalStyle`
  body { 
    background: ${theme.colors.background}; 
    font-family: ${theme.typography.fontFamily}; 
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

/* =============== PAGE STRUCTURE (matching BrowseTasks) =============== */
const Page = styled.div`
  background: ${theme.colors.background};
  min-height: 100vh;
  padding: 64px 0;
  animation: ${fadeIn} 0.6s ease;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Main task details container (matching BrowseTasks Details component)
const Details = styled.main`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  box-shadow: none;
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeIn} 0.25s ease;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

// Sticky header section
const StickyHead = styled.div`
  position: sticky;
  top: 0;
  background: ${theme.colors.surface};
  backdrop-filter: none;
  border-bottom: 1px solid ${theme.colors.border};
  border-radius: 12px 12px 0 0;
  margin-bottom: 32px;
  padding: 20px 24px;
  z-index: 10;
  box-shadow: none;
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    margin-bottom: 24px;
  }
  
  a {
    text-decoration: none;
    color: #0f172a;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.025em;
    text-transform: capitalize;
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }
`;

const TaskStatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0;
  
  ${props => {
    switch (props.status?.toLowerCase()) {
      case 'active':
      case 'open':
        return `
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        `;
      case 'in_progress':
      case 'in-progress':
        return `
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #fde68a;
        `;
      case 'completed':
        return `
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #bfdbfe;
        `;
      case 'cancelled':
      case 'canceled':
        return `
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        `;
      case 'paused':
        return `
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
        `;
      default:
        return `
          background: #f8fafc;
          color: #475569;
          border: 1px solid #e2e8f0;
        `;
    }
  }}
`;

// Poster row components (matching BrowseTasks)
const PosterRow = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #64748b;
  font-size: 0.92rem;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PosterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const ManageTaskButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  transition: border 0.12s, background 0.12s, color 0.12s;

  &:hover {
    background: #eff6ff;
    border-color: #3b82f6;
    color: #3b82f6;
  }

  @media (max-width: 640px) {
    padding: 6px 10px;
  }
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  background: #eff6ff;
  color: #3b82f6;
  display: grid;
  place-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 640px) {
    width: 26px;
    height: 26px;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 32px 0;
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const SoftStat = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 10px 12px;

  .label {
    color: #64748b;
    font-weight: 600;
    font-size: 0.9rem;
  }
  .value {
    color: #0f172a;
    font-weight: 800;
    font-size: 0.96rem;
  }

  @media (max-width: 640px) {
    padding: 12px 14px;
    .label {
      font-size: 0.86rem;
    }
    .value {
      font-size: 0.94rem;
    }
  }
`;

const ContentArea = styled.div`
  padding: 0 32px 40px;
  @media (max-width: 1024px) {
    padding: 0 16px 24px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 20px 0 16px;
`;

const Primary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${theme.colors.primary};
  color: #fff;
  border: 1px solid ${theme.colors.primary};
  border-radius: 8px;
  padding: 7px 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: none;
  transition: background 0.1s ease;
  
  &:hover {
    background: ${theme.colors.primaryHover};
  }
`;

const Ghost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: ${theme.colors.textBody};
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  padding: 7px 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
  
  &:hover {
    background: #f8fafc;
    color: #475569;
  }
`;

const ReportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  color: #94a3b8;
  border: none;
  padding: 4px 8px;
  font-weight: 500;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #64748b;
    text-decoration: underline;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const Section = styled.section`
  padding: 20px 0 24px;
  animation: ${slideInUp} 0.2s ease;

  & + & {
    border-top: 1px solid ${theme.colors.border};
    margin-top: 8px;
    padding-top: 22px;
  }

  @media (max-width: 640px) {
    padding: 16px 0 20px;
    & + & {
      margin-top: 14px;
      padding-top: 20px;
    }
  }
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 10px;
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;

  @media (max-width: 640px) {
    font-size: 0.96rem;
    gap: 8px;
    margin-bottom: 8px;
  }
`;

const Body = styled.p`
  color: #475569;
  line-height: 1.7;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  @media (max-width: 640px) {
    line-height: 1.6;
    font-size: 0.975rem;
  }
`;

const TagWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 640px) {
    gap: 8px;
  }
`;

const TimePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textBody};
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;

  @media (max-width: 640px) {
    padding: 7px 10px;
    font-size: 0.9rem;
  }
`;

const Thumb = styled.img`
  height: 84px;
  width: 128px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e2e8f0;

  @media (max-width: 640px) {
    height: 76px;
    width: 116px;
    border-radius: 8px;
  }
`;

const Empty = styled.div`
  padding: 16px;
  text-align: center;
  color: ${theme.colors.textMuted};
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  background: #ffffff;

  @media (max-width: 640px) {
    padding: 18px;
    border-radius: 12px;
  }
`;

const MobileFooterSpacer = styled.div`
  height: 80px;
  @media (min-width: 768px) {
    display: none;
  }
`;

/* =============== REPORT MODAL =============== */
const ReportModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 500000;
`;

const ReportModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  max-width: 400px;
  width: 90%;
  margin: 20px;
  box-shadow: ${theme.shadows.md};
`;

const ReportModalTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
`;

const ReportTypeOption = styled.div`
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReportOptionTitle = styled.div`
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const ReportOptionDescription = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const ReportModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const ReportModalButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &.cancel {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    color: #374151;

    &:hover {
      background: #e5e7eb;
    }
  }

  &.submit {
    background: #dc2626;
    border: 1px solid #dc2626;
    color: white;

    &:hover {
      background: #b91c1c;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

// User's own offer card (compact inline style for ButtonRow - matches Primary button height)
const UserOfferCard = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  color: #166534;
  border: 1px solid #86efac;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
`;

const UserOfferTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  color: #166534;
`;

const UserOfferAmount = styled.span`
  font-weight: 700;
  color: #166534;
`;

const EditOfferButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 3px 8px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  margin-left: 4px;

  &:hover {
    background: #166534;
    color: #ffffff;
  }
`;

// Edit button variant for use inside offer cards (green styling on white background)
const EditOfferCardButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 5px 10px;
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #166534;
    color: #ffffff;
    border-color: #166534;
  }
`;

// Form components (matching BrowseTasks)
const OfferFormCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid ${theme.colors.border};
  padding: 16px;
  box-shadow: none;
  animation: ${slideInUp} 0.25s ease;
`;

const FormField = styled.div`
  margin-bottom: 18px;
  label {
    display: block;
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: #475569;
    & > span.required {
      color: #ef4444;
      margin-left: 2px;
    }
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${props => props.$hasError ? '#ef4444' : '#e2e8f0'};
  background: #fff;
  transition: all 0.2s ease;
  
  ${props => props.$hasError && css`
    animation: shake 0.5s ease-in-out;
  `}
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : '#3b82f6'};
    box-shadow: ${props => props.$hasError ? 'none' : '0 0 0 2px #eff6ff'};
  }
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }
`;

const Textarea = styled(Input).attrs({ as: "textarea" })`
  resize: vertical;
  min-height: 110px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const SafetyTipComponent = styled.div`
  background: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  
  .tip-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    
    .icon {
      font-size: 1.2rem;
    }
    
    .title {
      font-weight: 700;
      color: #1e40af;
      font-size: 0.95rem;
    }
  }
  
  .content {
    color: #1e40af;
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

// Tab components (matching BrowseTasks)
const TabContainer = styled.div`
  display: flex;
  gap: 6px;
  margin: 0 0 20px;
  background: #ffffff;
  border-radius: 10px;
  padding: 4px;
  border: 1px solid ${theme.colors.border};
  box-shadow: none;
  width: fit-content;
  max-width: 400px;
`;

const TabButton = styled.button`
  background: ${(props) => (props.$active ? theme.colors.primaryLight : "transparent")};
  border: 1px solid ${(props) => (props.$active ? theme.colors.primaryLight : "transparent")};
  border-radius: 8px;
  padding: 6px 10px;
  font-weight: 600;
  font-size: 0.85rem;
  color: ${(props) =>
    props.$active ? theme.colors.primary : theme.colors.textMuted};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  box-shadow: none;
  white-space: nowrap;

  &:hover {
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  }
`;

const ExperienceTag = styled.div`
  display: inline-flex;
  align-items: center;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primaryText};
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.78rem;
  border: 1px solid rgba(59, 130, 246, 0.2);
  white-space: nowrap;
`;

// Offer and Question cards (matching BrowseTasks)
const OfferCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  animation: ${slideInUp} 0.2s ease;
  box-shadow: none;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
  }
`;

const OfferHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

const OfferPrice = styled.div`
  margin-left: auto;
  font-weight: 800;
  font-size: 1.25rem;
  color: ${theme.colors.textSecondary};
  padding: 8px 14px;
  border-radius: 10px;
  letter-spacing: -0.02em;
`;

const OfferUserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const OfferUserName = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: ${theme.colors.textPrimary};
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 8px;

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const OfferTimestamp = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.textMuted};
`;

const OfferProposal = styled.div`
  color: #334155;
  line-height: 1.7;
  font-size: 0.95rem;
  padding: 12px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const OfferSection = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const OfferSectionTitle = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: ${theme.colors.textPrimary};
  margin-bottom: 6px;
`;

const OfferSectionContent = styled.div`
  font-size: 0.92rem;
  color: ${theme.colors.textMuted};
  line-height: 1.5;
`;

const OfferMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${theme.colors.border};
`;

const OfferMetaItem = styled.div`
  .label {
    color: ${theme.colors.textMuted};
    font-weight: 600;
    font-size: 0.85rem;
    margin-bottom: 4px;
  }

  .value {
    font-weight: 700;
    color: ${theme.colors.textBody};
    font-size: 0.9rem;
  }
`;

const PortfolioLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const PortfolioLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: ${theme.colors.textBody};
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
  border: 1px solid #e2e8f0;

  &:hover {
    background: ${theme.colors.primaryLight};
    border-color: ${theme.colors.primary};
    text-decoration: none;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const QuestionCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  animation: ${slideInUp} 0.2s ease;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const QuestionUserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const QuestionUserName = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const QuestionBody = styled(OfferProposal)``;

const QuestionMeta = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  display: flex;
  align-items: center;
  margin-top: 4px;
`;

const QuestionText = styled.div`
  color: #334155;
  line-height: 1.7;
  font-size: 0.95rem;
  padding: 12px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const QuestionIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  flex-shrink: 0;
`;

const RepliesContainer = styled.div`
  margin-top: 0;
  margin-left: 48px;
  padding: 12px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border};
`;

const ReplyItem = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ReplyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const ReplyUserName = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: #0f172a;
  cursor: pointer;
  &:hover {
    color: #3b82f6;
  }
`;

const ReplyTimestamp = styled.div`
  font-size: 0.75rem;
  color: #64748b;
`;

const ReplyText = styled.div`
  font-size: 0.9rem;
  color: #334155;
  line-height: 1.6;
`;


// Reply form styled components (matching ManageTask)
const ReplyForm = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const ReplyTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px #eff6ff;
  }

  &::placeholder {
    color: #64748b;
  }
`;

const ReplyActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ReplyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.12s ease;

  &.primary {
    background: ${theme.colors.primary};
    color: white;
    border: 1px solid #3b82f6;

    &:hover {
      background: ${theme.colors.primaryHover};
    }
  }

  &.secondary {
    background: white;
    color: #334155;
    border: 1px solid #e2e8f0;

    &:hover {
      background: #eff6ff;
      color: #3b82f6;
    }
  }
`;

const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  height: ${(p) => p.h || "14px"};
  width: ${(p) => p.w || "100%"};

  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;

/* =============== OFFER CHAT STYLES =============== */
const OfferChatSection = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  animation: ${slideInUp} 0.25s ease;
`;

// OfferChat wrapper for inside offer cards (task owner view)
const OfferChat = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed ${theme.colors.border};
`;

const OfferChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  font-size: 0.95rem;
`;

const OfferChatCount = styled.span`
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
`;

const OfferChatToggle = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: ${theme.colors.primaryText};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  margin-left: auto;

  &:hover {
    text-decoration: underline;
  }
`;

const OfferChatEmpty = styled.div`
  color: ${theme.colors.textMuted};
  font-size: 0.85rem;
  margin-bottom: 6px;
`;

const OfferChatMessages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const OfferChatMessage = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const OfferChatBubble = styled.div`
  background: #ffffff;
  border: 1px solid ${theme.colors.border};
  border-radius: 10px;
  padding: 6px 8px;
  max-width: 520px;
`;

const OfferChatMeta = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 0.8rem;
  color: #64748b;
  margin-bottom: 4px;
`;

const OfferChatText = styled.div`
  font-size: 0.85rem;
  color: ${theme.colors.textBody};
  line-height: 1.35;
`;

const OfferChatInputForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

const OfferChatTextarea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px #eff6ff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const OfferChatActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const OfferChatButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  background: ${theme.colors.primary};
  color: white;
  border: 1px solid ${theme.colors.primary};
  box-shadow: none;

  &:hover:not(:disabled) {
    background: ${theme.colors.primaryHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* =============== FORM COMPONENTS =============== */
// Offer Form Component
const OfferFormComponent = ({ task, onCancel, onOfferSubmitted, existingOffer }) => {
  const [formData, setFormData] = useState({
    rate: existingOffer?.offer_amount?.toString() || "",
    proposal: existingOffer?.proposal || "",
    duration: existingOffer?.duration || "",
    completionDate: existingOffer?.completion_date || "",
    yearsExperience: existingOffer?.years_experience?.toString() || "",
    previousWork: existingOffer?.previous_similar_work || "",
    portfolioLinks: existingOffer?.portfolio_links?.length > 0 ? existingOffer.portfolio_links : [""],
    clientQuestions: existingOffer?.client_questions || "",
  });
  const isEditing = !!existingOffer;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handlePortfolioLinkChange = (i, v) => {
    const links = [...formData.portfolioLinks];
    links[i] = v;
    setFormData((p) => ({ ...p, portfolioLinks: links }));
  };

  const addPortfolioLink = () =>
    setFormData((p) => ({ ...p, portfolioLinks: [...p.portfolioLinks, ""] }));

  const handleSubmit = async () => {
    const errors = {};
    if (!formData.rate || formData.rate.trim() === "") {
      errors.rate = true;
    }
    if (!formData.proposal || formData.proposal.trim() === "") {
      errors.proposal = true;
    }
    
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      setSubmitError("Budget and Proposal are required.");
      return;
    }
    
    setSubmitError("");
    setIsSubmitting(true);
    
    try {
      const payload = {
        task_id: task.id,
        offer_amount: formData.rate,
        proposal: formData.proposal,
        duration: formData.duration,
        completion_date: formData.completionDate,
        years_experience: formData.yearsExperience,
        previous_similar_work: formData.previousWork,
        portfolio_links: formData.portfolioLinks.filter((x) => x.trim()),
        client_questions: formData.clientQuestions,
      };
      
      const { data } = await request("POST", API_CONSTANT_MAP.create_task_offer, payload);
      
      if (data?.success) {
        onOfferSubmitted(data.offer);
        onCancel();
      } else {
        setSubmitError(data.error?.message || "Failed to submit offer. Please try again.");
      }
    } catch (error) {
      setSubmitError("Failed to submit offer. Please try again.");
      console.error('Failed to submit offer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OfferFormCard>
      <SectionTitle>{isEditing ? "Edit Your Offer" : "Your Proposal"}</SectionTitle>

      <SafetyTipComponent>
        <div className="tip-header">
          <span className="icon">🛡️</span>
          <span className="title">Stay Safe While Making Offers</span>
        </div>
        <div className="content">
          Read the task details carefully and ask questions if unclear. Never pay upfront or share personal banking information. Keep all communication on the platform.
        </div>
      </SafetyTipComponent>

      {submitError && (
        <div style={{ color: '#ef4444', marginBottom: '16px', fontSize: '0.9rem' }}>
          {submitError}
        </div>
      )}

      <FormRow>
        <FormField>
          <label>
            Your Budget <span className="required">*</span>
          </label>
          <Input
            type="number"
            name="rate"
            value={formData.rate}
            onChange={handleChange}
            placeholder="Enter amount in USD"
            $hasError={validationErrors.rate}
          />
        </FormField>
        <FormField>
          <label>Duration</label>
          <Input
            type="text"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            placeholder="e.g., 2 weeks"
          />
        </FormField>
      </FormRow>

      <FormRow>
        <FormField>
          <label>Years of Relevant Experience</label>
          <Input
            type="number"
            name="yearsExperience"
            value={formData.yearsExperience}
            onChange={handleChange}
            placeholder="e.g., 5"
          />
        </FormField>
        <FormField>
          <label>Proposed Completion Date</label>
          <Input
            name="completionDate"
            value={formData.completionDate}
            onChange={handleChange}
            type="date"
          />
        </FormField>
      </FormRow>

      <FormField>
        <label>
          Why are you the best fit? <span className="required">*</span>
        </label>
        <Textarea
          name="proposal"
          value={formData.proposal}
          onChange={handleChange}
          placeholder="Describe your approach and why you're the right person for this task..."
          $hasError={validationErrors.proposal}
        />
      </FormField>

      <FormField>
        <label>Describe Previous Similar Work</label>
        <Textarea
          name="previousWork"
          value={formData.previousWork}
          onChange={handleChange}
          placeholder="Briefly describe 1–2 past projects."
        />
      </FormField>

      <FormField>
        <label>Portfolio / Work Samples</label>
        {formData.portfolioLinks.map((link, i) => (
          <Input
            key={i}
            value={link}
            onChange={(e) => handlePortfolioLinkChange(i, e.target.value)}
            placeholder="https://your-portfolio.com"
            style={{ marginBottom: 8 }}
          />
        ))}
        <button
          type="button"
          onClick={addPortfolioLink}
          style={{
            background: "none",
            border: 0,
            cursor: "pointer",
            color: theme.colors.primaryText,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: ".9rem",
          }}
        >
          <PlusCircle size={16} /> Add another link
        </button>
      </FormField>

      <FormField>
        <label>Questions for the Client (optional)</label>
        <Textarea
          name="clientQuestions"
          value={formData.clientQuestions}
          onChange={handleChange}
          placeholder="Anything you need clarified?"
        />
      </FormField>

      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
        <Primary onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (isEditing ? 'Updating...' : 'Submitting...') : (isEditing ? 'Update Offer' : 'Submit Offer')}
        </Primary>
        <Ghost onClick={onCancel}>Cancel</Ghost>
      </div>
    </OfferFormCard>
  );
};

// Question Form Component
const QuestionFormComponent = ({ task, onCancel, onQuestionSubmitted }) => {
  const [questionText, setQuestionText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleSubmit = async () => {
    if (!questionText.trim()) {
      setHasError(true);
      return;
    }
    
    setHasError(false);
    setIsSubmitting(true);
    
    try {
      const payload = {
        task_id: task.id,
        question_text: questionText.trim(),
      };
      
      const { data } = await request("POST", API_CONSTANT_MAP.submit_task_question, payload);
      
      if (data?.success) {
        onQuestionSubmitted(data.question);
        onCancel();
      } else {
        console.error('Question submission error:', data.error?.message || "Unknown error");
      }
    } catch (error) {
      console.error('Failed to submit question:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <OfferFormCard>
      <SectionTitle>Ask a Question</SectionTitle>
      <FormField>
        <Textarea
          value={questionText}
          onChange={(e) => {
            setQuestionText(e.target.value);
            if (hasError) setHasError(false);
          }}
          placeholder="What would you like to know about this task?"
          style={{ minHeight: "120px" }}
          $hasError={hasError}
        />
      </FormField>
      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
        <Primary onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit Question'}
        </Primary>
        <Ghost onClick={onCancel}>Cancel</Ghost>
      </div>
    </OfferFormCard>
  );
};

// Activity Component (matching BrowseTasks)
const Activity = ({
  activeTab,
  setActiveTab,
  offers,
  questions = [],
  isOffersLoading,
  isQuestionsLoading,
  task,
  state,
  replyingTo,
  setReplyingTo,
  replyText,
  setReplyText,
  replySubmitting,
  handleReplySubmit,
  handleReport,
  editingQuestion,
  editQuestionText,
  setEditQuestionText,
  editSubmitting,
  handleEditQuestion,
  handleEditSubmit,
  handleCancelEdit,
  editingReply,
  editReplyText,
  setEditReplyText,
  editReplySubmitting,
  handleEditReply,
  handleEditReplySubmit,
  handleCancelEditReply,
  onEditOffer,
  // Offer chat props
  isPoster,
  isTaskCancelled,
  offerQuestionsById,
  offerQuestionsLoadingById,
  offerChatExpandedById,
  setOfferChatExpandedById,
  offerQuestionTextById,
  setOfferQuestionTextById,
  offerQuestionSubmittingById,
  handleOfferQuestionSubmit,
}) => {
  return (
    <Section id="activity">
      <TabContainer>
        <TabButton
          $active={activeTab === "offers"}
          onClick={() => setActiveTab("offers")}
        >
          Offers ({offers.length})
        </TabButton>
        <TabButton
          $active={activeTab === "qa"}
          onClick={() => setActiveTab("qa")}
        >
          Q & A ({questions?.length || 0})
        </TabButton>
      </TabContainer>
      
      {activeTab === "offers" ? (
        offers.length ? (
          offers.map((offer, i) => (
            <OfferCard key={offer.id} style={{ animationDelay: `${i * 80}ms` }}>
              <OfferHeader>
                <AvatarComponent
                  src={offer.user?.avatar}
                  firstName={offer.user?.first_name}
                  lastName={offer.user?.last_name}
                  name={offer.user?.username}
                  userId={offer.user?.id}
                  size="32px"
                />
                <OfferUserInfo>
                  <OfferUserName>
                    <Link to={`/users/${offer.user?.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {offer.user?.first_name && offer.user?.last_name
                        ? `${offer.user.first_name} ${offer.user.last_name}`.trim().toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
                        : (offer.user?.username || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                    </Link>
                    <UserRating
                      userId={offer.user?.id}
                      averageRating={offer.user?.average_rating}
                      totalReviews={offer.user?.total_reviews}
                    />
                  </OfferUserName>
                  <OfferTimestamp>
                    {formatDistanceToNow(parseDate(offer.created_at), {
                      addSuffix: true,
                    })}
                  </OfferTimestamp>
                </OfferUserInfo>
                <OfferPrice>${offer.offer_amount}</OfferPrice>
              </OfferHeader>

              <OfferProposal>{offer.proposal}</OfferProposal>

              {offer.previous_similar_work && (
                <OfferSection>
                  <OfferSectionTitle>Similar Work</OfferSectionTitle>
                  <OfferSectionContent>
                    {offer.previous_similar_work}
                  </OfferSectionContent>
                </OfferSection>
              )}

              {/* Portfolio links as simple buttons */}
              {offer.portfolio_links && offer.portfolio_links.length > 0 && (
                <PortfolioLinks>
                  {offer.portfolio_links
                    .filter(link => link && isValidURL(link))
                    .slice(0, 3)
                    .map((link, index) => (
                      <PortfolioLink
                        key={index}
                        href={normalizeURL(link)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink /> {getDomainName(link)}
                      </PortfolioLink>
                    ))}
                </PortfolioLinks>
              )}

              <OfferMeta>
                <OfferMetaItem>
                  <div className="label">Timeline</div>
                  <div className="value">{offer.duration || "N/A"}</div>
                </OfferMetaItem>
                {offer.years_experience && (
                  <ExperienceTag>
                    {offer.years_experience} yrs exp
                  </ExperienceTag>
                )}
                {/* Edit button for user's own offer */}
                {offer.user?.id === state?.user?.user_id && onEditOffer && (
                  <EditOfferCardButton
                    onClick={onEditOffer}
                    style={{ marginLeft: 'auto' }}
                  >
                    <Pencil size={12} /> Edit
                  </EditOfferCardButton>
                )}
                {/* Report button - only show if not user's own offer */}
                {offer.user?.id !== state?.user?.user_id && (
                  <ReportButton
                    onClick={() => handleReport('offer', offer.id)}
                    title="Report this offer"
                  >
                    <Flag /> Report
                  </ReportButton>
                )}
              </OfferMeta>

              {/* Offer Discussion - for task owner and offer owner to chat */}
              {(() => {
                const offerQuestions = offerQuestionsById[offer.id] || [];
                const isOfferQuestionsLoading = !!offerQuestionsLoadingById[offer.id];
                const isOfferChatExpanded = !!offerChatExpandedById[offer.id];
                const offerQuestionText = offerQuestionTextById[offer.id] || "";
                const isOfferSubmitting = !!offerQuestionSubmittingById[offer.id];
                // Both task owner and the tasker who made this offer can chat
                const isOfferOwner = offer.user?.id === state?.user?.user_id;
                const canPostOfferChat = isPoster || isOfferOwner;

                return (offerQuestions.length > 0 || canPostOfferChat || isOfferQuestionsLoading) ? (
                  <OfferChat>
                    <OfferChatHeader>
                      <MessageCircle size={16} />
                      Offer Discussion
                      <OfferChatCount>({offerQuestions.length})</OfferChatCount>
                      {(canPostOfferChat || offerQuestions.length > 0) && (
                        <OfferChatToggle
                          onClick={() =>
                            setOfferChatExpandedById((p) => ({
                              ...p,
                              [offer.id]: !isOfferChatExpanded,
                            }))
                          }
                        >
                          {offerQuestions.length === 0 && canPostOfferChat
                            ? "Start discussion"
                            : isOfferChatExpanded
                            ? "Hide"
                            : "View"}
                        </OfferChatToggle>
                      )}
                    </OfferChatHeader>
                    {isOfferQuestionsLoading ? (
                      <OfferChatEmpty>Loading messages...</OfferChatEmpty>
                    ) : offerQuestions.length > 0 && isOfferChatExpanded ? (
                      <OfferChatMessages>
                        {offerQuestions.flatMap((q) => {
                          const base = [{
                            id: `q-${q.id}`,
                            user_id: q.user_id,
                            user_name: q.user_name,
                            avatar: q.avatar,
                            email_verified: q.email_verified,
                            text: q.question_text || q.text,
                            created_at: q.created_at,
                          }];
                          const replies = (q.replies || []).map((reply) => ({
                            id: `r-${reply.id}`,
                            user_id: reply.user_id,
                            user_name: reply.user_name,
                            avatar: reply.avatar,
                            email_verified: reply.email_verified,
                            text: reply.reply_text || reply.text,
                            created_at: reply.created_at,
                          }));
                          return [...base, ...replies];
                        }).sort((a, b) => parseDate(a.created_at) - parseDate(b.created_at)).map((m) => (
                          <OfferChatMessage key={m.id}>
                            <AvatarComponent
                              src={m.avatar}
                              firstName={m.user_name}
                              userId={m.user_id}
                              size="28px"
                            />
                            <OfferChatBubble>
                              <OfferChatMeta>
                                <span style={{ fontWeight: 600 }}>
                                  {(m.user_name || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                                <span>·</span>
                                <span>
                                  {formatDistanceToNow(parseDate(m.created_at), {
                                    addSuffix: true,
                                  })}
                                </span>
                              </OfferChatMeta>
                              <OfferChatText>{m.text}</OfferChatText>
                            </OfferChatBubble>
                          </OfferChatMessage>
                        ))}
                      </OfferChatMessages>
                    ) : null}

                    {isOfferChatExpanded && canPostOfferChat && (
                      <OfferChatInputForm>
                        <OfferChatTextarea
                          value={offerQuestionText}
                          onChange={(e) =>
                            setOfferQuestionTextById((p) => ({
                              ...p,
                              [offer.id]: e.target.value,
                            }))
                          }
                          placeholder="Send a message about this offer..."
                          disabled={isTaskCancelled}
                        />
                        <OfferChatActions>
                          <OfferChatButton
                            onClick={() => handleOfferQuestionSubmit(offer.id)}
                            disabled={!offerQuestionText.trim() || isOfferSubmitting || isTaskCancelled}
                          >
                            {isOfferSubmitting ? "Sending..." : "Send Message"}
                          </OfferChatButton>
                        </OfferChatActions>
                      </OfferChatInputForm>
                    )}
                  </OfferChat>
                ) : null;
              })()}
            </OfferCard>
          ))
        ) : (
          <Empty>No offers yet — be the first to pitch!</Empty>
        )
      ) : (
        <div>
          {questions?.length ? (
            questions.map((q, i) => (
              <QuestionCard key={q.id} style={{ animationDelay: `${i * 80}ms` }}>
                <QuestionHeader>
                  <AvatarComponent
                    src={q.avatar}
                    name={q.user_name}
                    userId={q.user_id}
                    size="40px"
                  />
                  <QuestionUserInfo>
                    <QuestionUserName>
                      <Link to={`/users/${q.user_id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {q.user_name || "Anonymous User"}
                      </Link>
                      <UserRating
                        userId={q.user_id}
                        averageRating={q.average_rating}
                        totalReviews={q.total_reviews}
                      />
                    </QuestionUserName>
                    <QuestionMeta>
                      <span>{formatDistanceToNow(parseDate(q.created_at), { addSuffix: true })}</span>
                      {q.user_id === state?.user?.user_id && editingQuestion !== q.id && (
                        <span
                          onClick={() => handleEditQuestion(q)}
                          style={{
                            marginLeft: '8px',
                            color: '#6b7280',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                          onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                          onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                        >
                          · Edit
                        </span>
                      )}
                    </QuestionMeta>
                  </QuestionUserInfo>
                </QuestionHeader>
                {editingQuestion === q.id ? (
                  <ReplyForm style={{ marginBottom: '16px' }}>
                    <ReplyTextarea
                      value={editQuestionText}
                      onChange={(e) => setEditQuestionText(e.target.value)}
                      placeholder="Edit your question..."
                    />
                    <ReplyActions>
                      <ReplyButton
                        className="secondary"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </ReplyButton>
                      <ReplyButton
                        className="primary"
                        onClick={() => handleEditSubmit(q.id)}
                        disabled={!editQuestionText.trim() || editSubmitting}
                      >
                        {editSubmitting ? "Saving..." : "Save"}
                      </ReplyButton>
                    </ReplyActions>
                  </ReplyForm>
                ) : (
                  <QuestionText>{q.question_text || q.text}</QuestionText>
                )}
                {q.replies?.length > 0 && (
                  <RepliesContainer>
                    {q.replies.map((reply, idx) => (
                      <ReplyItem key={reply.id || idx}>
                        <ReplyHeader>
                          <AvatarComponent
                            src={reply.avatar}
                            name={reply.user_name}
                            userId={reply.user_id}
                            size="24px"
                          />
                          <ReplyUserName onClick={() => { window.location.href = `/users/${reply.user_id}`; }}>{reply.user_name}</ReplyUserName>
                          <ReplyTimestamp>
                            {reply.created_at && new Date(reply.created_at).toLocaleDateString()}
                            {reply.user_id === state?.user?.user_id && editingReply !== reply.id && (
                              <span
                                onClick={() => handleEditReply(reply)}
                                style={{
                                  marginLeft: '8px',
                                  color: '#6b7280',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => e.target.style.color = '#3b82f6'}
                                onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                              >
                                · Edit
                              </span>
                            )}
                          </ReplyTimestamp>
                        </ReplyHeader>
                        {editingReply === reply.id ? (
                          <ReplyForm style={{ marginTop: '8px', marginBottom: '16px', marginLeft: '0' }}>
                            <ReplyTextarea
                              value={editReplyText}
                              onChange={(e) => setEditReplyText(e.target.value)}
                              placeholder="Edit your reply..."
                            />
                            <ReplyActions>
                              <ReplyButton
                                className="secondary"
                                onClick={handleCancelEditReply}
                              >
                                Cancel
                              </ReplyButton>
                              <ReplyButton
                                className="primary"
                                onClick={() => handleEditReplySubmit(reply.id)}
                                disabled={!editReplyText.trim() || editReplySubmitting}
                              >
                                {editReplySubmitting ? "Saving..." : "Save"}
                              </ReplyButton>
                            </ReplyActions>
                          </ReplyForm>
                        ) : (
                          <ReplyText>{reply.reply_text || reply.text}</ReplyText>
                        )}
                      </ReplyItem>
                    ))}
                  </RepliesContainer>
                )}
                
                {/* Reply functionality - show for task poster or question asker */}
                {(task?.poster?.id === state?.user?.user_id || q.user_id === state?.user?.user_id) ? (
                  replyingTo === q.id ? (
                    <ReplyForm>
                      <ReplyTextarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write your reply..."
                      />
                      <ReplyActions>
                        <ReplyButton
                          className="secondary"
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText("");
                          }}
                        >
                          Cancel
                        </ReplyButton>
                        <ReplyButton
                          className="primary"
                          onClick={() => handleReplySubmit(q.id)}
                          disabled={!replyText.trim() || replySubmitting}
                        >
                          {replySubmitting ? "Submitting..." : "Submit Reply"}
                        </ReplyButton>
                      </ReplyActions>
                    </ReplyForm>
                  ) : (
                    <div style={{ marginTop: "12px" }}>
                      <ReplyButton
                        className="secondary"
                        onClick={() => setReplyingTo(q.id)}
                      >
                        Reply to Question
                      </ReplyButton>
                    </div>
                  )
                ) : null}
                
                {/* Report button */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  marginTop: '12px', 
                  paddingTop: '12px', 
                  borderTop: '1px solid #f1f5f9' 
                }}>
                  <ReportButton
                    onClick={() => handleReport('task', q.id)}
                    title="Report this question"
                    style={{ fontSize: '0.75rem' }}
                  >
                    <Flag size={12} /> Report
                  </ReportButton>
                </div>
              </QuestionCard>
            ))
          ) : (
            <Empty>No questions yet — be the first to ask!</Empty>
          )}
        </div>
      )}
    </Section>
  );
};

/* =============== PAGE =============== */
function TaskPage({ state }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form states (matching BrowseTasks)
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);

  // Reply states (matching ManageTask)
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);

  // Edit question states
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editQuestionText, setEditQuestionText] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Edit reply states
  const [editingReply, setEditingReply] = useState(null);
  const [editReplyText, setEditReplyText] = useState("");
  const [editReplySubmitting, setEditReplySubmitting] = useState(false);

  // Tab and data states
  const [activeTab, setActiveTab] = useState("qa");
  const [offers, setOffers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [isOffersLoading, setIsOffersLoading] = useState(false);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);

  // Track user's own offer
  const [userOffer, setUserOffer] = useState(null);

  // Offer chat state (for tasker's own offer discussion)
  const [offerChatExpanded, setOfferChatExpanded] = useState(false);
  const [offerChatMessages, setOfferChatMessages] = useState([]);
  const [offerChatLoading, setOfferChatLoading] = useState(false);
  const [offerChatText, setOfferChatText] = useState("");
  const [offerChatSubmitting, setOfferChatSubmitting] = useState(false);

  // Offer questions state (for task owner viewing offers tab)
  const [offerQuestionsById, setOfferQuestionsById] = useState({});
  const [offerQuestionsLoadingById, setOfferQuestionsLoadingById] = useState({});
  const [offerChatExpandedById, setOfferChatExpandedById] = useState({});
  const [offerQuestionTextById, setOfferQuestionTextById] = useState({});
  const [offerQuestionSubmittingById, setOfferQuestionSubmittingById] = useState({});

  // Report modal state
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportEntity, setReportEntity] = useState({ type: null, id: null });
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // Helper function to check if task is cancelled
  const isTaskCancelled = task?.status?.toLowerCase() === 'cancelled' || task?.status?.toLowerCase() === 'canceled';

  // Check if current user is the task poster
  const isPoster = state.user?.user_id === task?.poster?.id;

  // Load offer chat messages for the user's offer
  const loadOfferChatMessages = useCallback(async (offerId, taskId) => {
    if (!offerId || !taskId) return;
    setOfferChatLoading(true);
    try {
      const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_questions, {
        task_id: taskId,
        offer_id: offerId,
      });
      setOfferChatMessages(data?.success ? data.questions : []);
    } catch (err) {
      console.error("Error fetching offer chat messages:", err);
    } finally {
      setOfferChatLoading(false);
    }
  }, []);

  // Submit a message in the offer chat
  const handleOfferChatSubmit = async () => {
    const text = offerChatText.trim();
    if (!text || offerChatSubmitting || !userOffer) return;

    setOfferChatSubmitting(true);
    try {
      const response = await request("POST", API_CONSTANT_MAP.submit_task_question, {
        task_id: id,
        offer_id: userOffer.id,
        question_text: text,
      });

      if (response.data?.success) {
        setOfferChatText("");
        await loadOfferChatMessages(userOffer.id, id);
      } else {
        alert("Failed to post message. Please try again.");
      }
    } catch (error) {
      console.error("Error posting offer message:", error);
      alert("Error posting message. Please try again.");
    } finally {
      setOfferChatSubmitting(false);
    }
  };

  // Load offer questions for a specific offer (task owner view)
  const loadOfferQuestions = useCallback(async (offerId) => {
    if (!id || !offerId) return;
    setOfferQuestionsLoadingById((p) => ({ ...p, [offerId]: true }));
    try {
      const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_questions, {
        task_id: id,
        offer_id: offerId,
      });
      setOfferQuestionsById((p) => ({
        ...p,
        [offerId]: data?.success ? data.questions : [],
      }));
    } catch (err) {
      console.error("Error fetching offer questions:", err);
    } finally {
      setOfferQuestionsLoadingById((p) => ({ ...p, [offerId]: false }));
    }
  }, [id]);

  // Submit a message in an offer chat (task owner view)
  const handleOfferQuestionSubmit = async (offerId) => {
    const text = (offerQuestionTextById[offerId] || "").trim();
    if (!text || offerQuestionSubmittingById[offerId]) return;
    try {
      setOfferQuestionSubmittingById((p) => ({ ...p, [offerId]: true }));
      const payload = {
        task_id: id,
        offer_id: offerId,
        question_text: text,
      };
      const { data } = await request(
        "POST",
        API_CONSTANT_MAP.submit_task_question,
        payload
      );
      if (data?.success) {
        setOfferQuestionsById((p) => ({
          ...p,
          [offerId]: [data.question, ...(p[offerId] || [])],
        }));
        setOfferQuestionTextById((p) => ({ ...p, [offerId]: "" }));
      } else {
        alert(data?.error?.message || "Failed to post message.");
      }
    } catch (error) {
      console.error("Failed to submit offer message:", error);
      alert("Failed to submit message. Please try again.");
    } finally {
      setOfferQuestionSubmittingById((p) => ({ ...p, [offerId]: false }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const resp = await request("POST", API_CONSTANT_MAP.fetch_single_task, {
          task_id: id,
        });
        if (resp.data?.success) {
          console.log("TaskPage: Full task data received:", resp.data.task);
          console.log("TaskPage: Poster data specifically:", resp.data.task.poster);
          setTask(resp.data.task);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Reset form states when task changes
  useEffect(() => {
    setActiveTab("qa");
    setShowOfferForm(false);
    setShowQuestionForm(false);
  }, [task?.id]);

  // Smart default tab based on available content (matching BrowseTasks)
  useEffect(() => {
    if (task && !isOffersLoading && !isQuestionsLoading) {
      const hasQuestions = questions && questions.length > 0;
      const hasOffers = offers && offers.length > 0;
      
      // Priority: Offers > Questions > Default to questions tab
      if (hasOffers) {
        setActiveTab("offers");
      } else if (hasQuestions) {
        setActiveTab("qa");
      }
      // If neither has content, stay on questions tab (default for new tasks)
    }
  }, [task, offers, questions, isOffersLoading, isQuestionsLoading]);

  // Fetch offers and questions when task loads
  useEffect(() => {
    if (task?.id) {
      fetchOffers();
      fetchQuestions();
    }
  }, [task?.id]);

  const fetchOffers = async () => {
    if (!task?.id) return;
    setIsOffersLoading(true);
    try {
      const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_offers, {
        task_id: task.id,
      });
      if (data?.success) {
        const allOffers = data.offers || [];
        setOffers(allOffers);

        // Find user's own offer if logged in
        if (state?.user?.user_id) {
          const myOffer = allOffers.find(offer => offer.user?.id === state.user.user_id);
          setUserOffer(myOffer || null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch offers:', error);
    } finally {
      setIsOffersLoading(false);
    }
  };

  const fetchQuestions = async () => {
    if (!task?.id) return;
    setIsQuestionsLoading(true);
    try {
      const { data } = await request("POST", API_CONSTANT_MAP.fetch_task_questions, {
        task_id: task.id,
      });
      if (data?.success) {
        setQuestions(data.questions || []);
      }
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setIsQuestionsLoading(false);
    }
  };

  // Pre-load offer chat messages when user has an offer
  useEffect(() => {
    if (userOffer && !offerChatMessages.length && !offerChatLoading) {
      loadOfferChatMessages(userOffer.id, id);
    }
  }, [userOffer, id, offerChatMessages.length, offerChatLoading, loadOfferChatMessages]);

  // Handle URL params for deep linking to offer chat (e.g., from email notification)
  useEffect(() => {
    const offerIdParam = searchParams.get("offer");
    if (offerIdParam && userOffer && String(userOffer.id) === offerIdParam) {
      // Expand the offer chat for the user's offer
      setOfferChatExpanded(true);
    }
  }, [searchParams, userOffer]);

  // Pre-load offer questions for all offers (task owner) or user's own offer (offer owner)
  useEffect(() => {
    if (!offers?.length) return;
    const userId = state?.user?.user_id;
    offers.forEach((offer) => {
      if (!offer?.id) return;
      // Load for task owner (all offers) or for offer owner (their own offer)
      const isThisOfferOwner = offer.user?.id === userId;
      if (!isPoster && !isThisOfferOwner) return; // Skip if not task owner and not this offer's owner
      if (offerQuestionsById[offer.id]) return; // Skip if already loaded
      if (offerQuestionsLoadingById[offer.id]) return; // Skip if loading
      loadOfferQuestions(offer.id);
    });
  }, [offers, isPoster, state?.user?.user_id, offerQuestionsById, offerQuestionsLoadingById, loadOfferQuestions]);

  // Handle URL params for deep linking to offer chat (task owner view)
  useEffect(() => {
    const offerIdParam = searchParams.get("offer");
    if (offerIdParam && isPoster && offers?.length) {
      const targetOffer = offers.find(o => String(o.id) === offerIdParam);
      if (targetOffer) {
        setActiveTab("offers");
        setOfferChatExpandedById((p) => ({ ...p, [targetOffer.id]: true }));
      }
    }
  }, [searchParams, isPoster, offers]);

  const addNewOffer = (offer) => {
    // If this is the user's offer, update userOffer
    if (offer.user?.id === state?.user?.user_id) {
      setUserOffer(offer);
      // Update existing offer in the list or add new
      setOffers(prev => {
        const existingIndex = prev.findIndex(o => o.id === offer.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = offer;
          return updated;
        }
        return [offer, ...prev];
      });
    } else {
      setOffers(prev => [offer, ...prev]);
    }
  };

  const addNewQuestion = (question) => {
    setQuestions(prev => [question, ...prev]);
  };

  // Reply functionality (matching ManageTask)
  const handleReplySubmit = async (questionId) => {
    if (!replyText.trim() || replySubmitting) return;

    try {
      setReplySubmitting(true);
      const response = await request(
        "POST",
        API_CONSTANT_MAP.post_task_question_reply,
        {
          question_id: questionId,
          reply_text: replyText.trim(),
        }
      );

      if (response.data?.success) {
        // Refresh questions to show the new reply
        const questionsResp = await request(
          "POST",
          API_CONSTANT_MAP.fetch_task_questions,
          {
            task_id: id,
          }
        );
        setQuestions(questionsResp.data?.questions || []);

        // Reset reply form
        setReplyingTo(null);
        setReplyText("");
      } else {
        alert("Failed to submit reply. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
      alert("Error submitting reply. Please try again.");
    } finally {
      setReplySubmitting(false);
    }
  };

  // Edit question handler
  const handleEditQuestion = (question) => {
    setEditingQuestion(question.id);
    setEditQuestionText(question.question_text || question.text);
    // Close reply form if open
    setReplyingTo(null);
    setReplyText("");
  };

  const handleEditSubmit = async (questionId) => {
    if (!editQuestionText.trim() || editSubmitting) return;

    try {
      setEditSubmitting(true);
      const response = await request(
        "POST",
        API_CONSTANT_MAP.edit_task_question,
        {
          question_id: questionId,
          question_text: editQuestionText.trim(),
        }
      );

      if (response.data?.success) {
        // Refresh questions to show the updated text
        const questionsResp = await request(
          "POST",
          API_CONSTANT_MAP.fetch_task_questions,
          {
            task_id: id,
          }
        );
        setQuestions(questionsResp.data?.questions || []);

        // Reset edit form
        setEditingQuestion(null);
        setEditQuestionText("");
      } else {
        alert(response.data?.error?.message || "Failed to update question. Please try again.");
      }
    } catch (error) {
      console.error("Error updating question:", error);
      alert("Error updating question. Please try again.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setEditQuestionText("");
  };

  // Edit reply handlers
  const handleEditReply = (reply) => {
    setEditingReply(reply.id);
    setEditReplyText(reply.reply_text || reply.text);
    // Close question edit form if open
    setEditingQuestion(null);
    setEditQuestionText("");
  };

  const handleEditReplySubmit = async (replyId) => {
    if (!editReplyText.trim() || editReplySubmitting) return;

    try {
      setEditReplySubmitting(true);
      const response = await request(
        "POST",
        API_CONSTANT_MAP.edit_task_question_reply,
        {
          reply_id: replyId,
          reply_text: editReplyText.trim(),
        }
      );

      if (response.data?.success) {
        // Refresh questions to show the updated reply
        const questionsResp = await request(
          "POST",
          API_CONSTANT_MAP.fetch_task_questions,
          {
            task_id: id,
          }
        );
        setQuestions(questionsResp.data?.questions || []);

        // Reset edit form
        setEditingReply(null);
        setEditReplyText("");
      } else {
        alert(response.data?.error?.message || "Failed to update reply. Please try again.");
      }
    } catch (error) {
      console.error("Error updating reply:", error);
      alert("Error updating reply. Please try again.");
    } finally {
      setEditReplySubmitting(false);
    }
  };

  const handleCancelEditReply = () => {
    setEditingReply(null);
    setEditReplyText("");
  };

  // Report function - opens modal for type selection
  const handleReport = (entityType, entityId) => {
    if (!state?.user?.token) {
      alert('Please sign in to report content');
      return;
    }

    setReportEntity({ type: entityType, id: entityId });
    setSelectedReportType(null);
    setIsReportModalOpen(true);
  };

  // Submit report function
  const submitReport = async () => {
    if (!selectedReportType) return;

    setIsSubmittingReport(true);

    try {
      const response = await request(
        "POST",
        API_CONSTANT_MAP.submit_report,
        {
          entity_type: reportEntity.type,
          entity_id: reportEntity.id,
          report_type: selectedReportType,
          description: `Reported ${reportEntity.type} as ${selectedReportType}`,
        }
      );

      if (response.data?.success) {
        alert('Thank you for your report. We will review it and take appropriate action.');
        setIsReportModalOpen(false);
        setReportEntity({ type: null, id: null });
        setSelectedReportType(null);
      } else {
        alert(response.data?.error?.message || 'Failed to submit report');
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    }

    setIsSubmittingReport(false);
  };

  if (loading) {
    return (
      <>
        <GlobalStyle />
        <Page>
          <NavBar user={state.user} />
          <Container>
            <Details>
              {/* Sticky header skeleton */}
              <StickyHead>
                <div>
                  <Skeleton h="20px" w="20px" style={{ borderRadius: "4px" }} />
                </div>
                <div>
                  <Skeleton h="24px" w="60%" style={{ borderRadius: "6px" }} />
                </div>
                <PosterRow>
                  <PosterInfo>
                    <Skeleton h="16px" w="80px" style={{ borderRadius: "4px" }} />
                    <Skeleton h="32px" w="32px" style={{ borderRadius: "50%" }} />
                    <Skeleton h="16px" w="100px" style={{ borderRadius: "4px" }} />
                  </PosterInfo>
                </PosterRow>
              </StickyHead>

              <ContentArea>
                {/* Stats skeleton */}
                <StatGrid>
                <SoftStat>
                  <Skeleton h="18px" w="18px" />
                  <div>
                    <Skeleton h="12px" w="50px" style={{ marginBottom: "4px" }} />
                    <Skeleton h="16px" w="60px" />
                  </div>
                </SoftStat>
                <SoftStat>
                  <Skeleton h="18px" w="18px" />
                  <div>
                    <Skeleton h="12px" w="60px" style={{ marginBottom: "4px" }} />
                    <Skeleton h="16px" w="80px" />
                  </div>
                </SoftStat>
                <SoftStat>
                  <Skeleton h="18px" w="18px" />
                  <div>
                    <Skeleton h="12px" w="55px" style={{ marginBottom: "4px" }} />
                    <Skeleton h="16px" w="90px" />
                  </div>
                </SoftStat>
                </StatGrid>

                {/* Button row skeleton */}
                <ButtonRow>
                  <Skeleton h="38px" w="130px" style={{ borderRadius: "10px" }} />
                  <Skeleton h="38px" w="140px" style={{ borderRadius: "10px" }} />
                </ButtonRow>

                {/* Description skeleton */}
                <Section>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <Skeleton h="18px" w="18px" />
                  <Skeleton h="18px" w="150px" />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <Skeleton h="16px" w="100%" />
                  <Skeleton h="16px" w="95%" />
                  <Skeleton h="16px" w="87%" />
                  <Skeleton h="16px" w="60%" />
                </div>
              </Section>

              {/* Additional section skeleton */}
              <Section>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                  <Skeleton h="18px" w="18px" />
                  <Skeleton h="18px" w="100px" />
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <Skeleton h="36px" w="120px" style={{ borderRadius: "999px" }} />
                  <Skeleton h="36px" w="140px" style={{ borderRadius: "999px" }} />
                </div>
                </Section>
              </ContentArea>
            </Details>
          </Container>
        </Page>
      </>
    );
  }

  if (!task) {
    return (
      <Page>
        <NavBar user={state.user} />
        <Container>
          <Details>
            <ContentArea>
              <Empty>Task not found or could not be loaded.</Empty>
            </ContentArea>
          </Details>
          <MobileFooterSpacer />
        </Container>
      </Page>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Page>
        <NavBar user={state.user} />
        <Container>
          <Details>
            {/* Sticky header */}
            <StickyHead>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <a href={`/tasks/${task.id}`} style={{ textTransform: "capitalize", textDecoration: "none", color: "#0f172a", fontSize: "1.5rem", fontWeight: 800, lineHeight: 1.3, letterSpacing: "-0.025em" }}>
                  {task.title}
                </a>
                {task.status && (
                  <TaskStatusBadge status={task.status}>
                    {task.status.replace('_', ' ')}
                  </TaskStatusBadge>
                )}
              </div>
              <PosterRow>
                <PosterInfo>
                  <span>
                    Posted{" "}
                    {formatDistanceToNow(parseDate(task.created_at), {
                      addSuffix: true,
                    })}{" "}
                    by
                  </span>
                  <AvatarComponent
                    src={task.poster?.avatar}
                    firstName={task.poster?.first_name}
                    lastName={task.poster?.last_name}
                    name={task.poster?.username}
                    userId={task.poster?.id}
                    size="28px"
                  />
                  <span
                    style={{
                      color: "#0f172a",
                      textDecoration: "none",
                      transition: "text-decoration 0.2s ease",
                      fontWeight: 700,
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.textDecoration = "none";
                    }}
                    onClick={() => {
                      window.location.href = `/users/${task.poster?.id}`;
                    }}
                  >
                    {task.poster?.first_name && task.poster?.last_name
                      ? `${task.poster.first_name} ${task.poster.last_name}`
                      : task.poster?.username || "User"}
                  </span>
                </PosterInfo>
                {/* Show Manage Task button if user is the poster */}
                {isPoster && (
                  <ManageTaskButton onClick={() => navigate(`/tasks/${task.id}/manage`)}>
                    <Settings size={14} /> Manage Task
                  </ManageTaskButton>
                )}
              </PosterRow>
            </StickyHead>

            <ContentArea>
              <StatGrid>
                <SoftStat>
                  <div>
                    <DollarSign size={18} />
                  </div>
                  <div>
                    <div className="label">Budget</div>
                    <div className="value">${task.budget}{task.budget_type === 'hourly' ? '/hr' : ''}</div>
                  </div>
                </SoftStat>

                <SoftStat>
                  <div>
                    <MapPin size={18} />
                  </div>
                  <div>
                    <div className="label">Location</div>
                    <div className="value">
                      {task.task_type === "online"
                        ? "Remote"
                        : task.location || "Location not specified"}
                    </div>
                  </div>
                </SoftStat>

                {task.due_date && (
                  <SoftStat>
                    <div>
                      <Calendar size={18} />
                    </div>
                    <div>
                      <div className="label">Deadline</div>
                      <div className="value">
                        {format(parseDate(task.due_date), "MMM d, yyyy")}
                      </div>
                    </div>
                  </SoftStat>
                )}
              </StatGrid>
              <ButtonRow>
                {/* Show user's existing offer inline, or Submit an Offer button */}
                {userOffer && !showOfferForm && !isPoster ? (
                  <UserOfferCard>
                    <UserOfferTitle>
                      <Check size={14} /> Your Offer:
                    </UserOfferTitle>
                    <UserOfferAmount>${userOffer.offer_amount}</UserOfferAmount>
                    <EditOfferButton
                      onClick={() => {
                        if (isTaskCancelled) return;
                        setShowOfferForm(true);
                        setShowQuestionForm(false);
                        setActiveTab("offers");
                        setTimeout(() => {
                          document.getElementById('offer-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      disabled={isTaskCancelled}
                      style={{
                        opacity: isTaskCancelled ? 0.5 : 1,
                        cursor: isTaskCancelled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Pencil size={12} /> Edit
                    </EditOfferButton>
                  </UserOfferCard>
                ) : !userOffer && !isPoster ? (
                  <Primary
                    onClick={() => {
                      if (isTaskCancelled) return;
                      if (!state.user?.user_id) {
                        navigate("/signin");
                        return;
                      }
                      setShowOfferForm(true);
                      setShowQuestionForm(false);
                      setActiveTab("offers");
                    }}
                    disabled={isTaskCancelled}
                    style={{
                      opacity: isTaskCancelled ? 0.5 : 1,
                      cursor: isTaskCancelled ? 'not-allowed' : 'pointer'
                    }}
                    title={isTaskCancelled ? 'This task has been cancelled' : 'Submit an offer for this task'}
                  >
                    <Star size={16} /> Submit an Offer
                  </Primary>
                ) : null}
                <Ghost
                  onClick={() => {
                    if (isTaskCancelled) return;
                    if (!state.user?.user_id) {
                      navigate("/signin");
                      return;
                    }
                    setShowQuestionForm(true);
                    setShowOfferForm(false);
                    setActiveTab("qa");
                  }}
                  disabled={isTaskCancelled}
                  style={{
                    opacity: isTaskCancelled ? 0.5 : 1,
                    cursor: isTaskCancelled ? 'not-allowed' : 'pointer'
                  }}
                  title={isTaskCancelled ? 'This task has been cancelled' : 'Ask a question about this task'}
                >
                  <MessageCircle size={16} /> Ask a Question
                </Ghost>
                <ReportButton
                  onClick={() => handleReport('task', task.id)}
                  title="Report this task"
                >
                  <Flag size={14} /> Report
                </ReportButton>
              </ButtonRow>

              {showOfferForm && (
                <div id="offer-form-section" style={{ marginTop: 14 }}>
                  <OfferFormComponent
                    task={task}
                    existingOffer={userOffer}
                    onCancel={() => setShowOfferForm(false)}
                    onOfferSubmitted={(offer) => {
                      addNewOffer(offer);
                      setShowOfferForm(false);
                    }}
                  />
                </div>
              )}

              {showQuestionForm && (
                <div style={{ marginTop: 14 }}>
                  <QuestionFormComponent
                    task={task}
                    onCancel={() => setShowQuestionForm(false)}
                    onQuestionSubmitted={(question) => {
                      addNewQuestion(question);
                      setShowQuestionForm(false);
                    }}
                  />
                </div>
              )}

              {/* Offer Chat Section - shows for taskers who have an offer on this task */}
              {userOffer && !isPoster && !showOfferForm && userOffer.status !== "accepted" && (
                <OfferChatSection id="offer-chat-section">
                  <OfferChatHeader>
                    <MessageCircle size={18} />
                    <span>Offer Discussion</span>
                    <OfferChatCount>({offerChatMessages.length})</OfferChatCount>
                    <OfferChatToggle
                      onClick={() => setOfferChatExpanded(!offerChatExpanded)}
                    >
                      {offerChatMessages.length === 0
                        ? "Start discussion"
                        : offerChatExpanded
                        ? "Hide"
                        : "View"}
                    </OfferChatToggle>
                  </OfferChatHeader>

                  {offerChatLoading ? (
                    <OfferChatEmpty>Loading messages...</OfferChatEmpty>
                  ) : offerChatMessages.length > 0 && offerChatExpanded ? (
                    <OfferChatMessages>
                      {offerChatMessages
                        .flatMap((q) => {
                          const base = [{
                            id: `q-${q.id}`,
                            user_id: q.user_id,
                            user_name: q.user_name,
                            avatar: q.avatar,
                            email_verified: q.email_verified,
                            text: q.question_text || q.text,
                            created_at: q.created_at,
                          }];
                          const replies = (q.replies || []).map((reply) => ({
                            id: `r-${reply.id}`,
                            user_id: reply.user_id,
                            user_name: reply.user_name,
                            avatar: reply.avatar,
                            email_verified: reply.email_verified,
                            text: reply.reply_text || reply.text,
                            created_at: reply.created_at,
                          }));
                          return [...base, ...replies];
                        })
                        .sort((a, b) => parseDate(a.created_at) - parseDate(b.created_at))
                        .map((m) => (
                          <OfferChatMessage key={m.id}>
                            <AvatarComponent
                              src={m.avatar}
                              firstName={m.user_name}
                              userId={m.user_id}
                              size="32px"
                            />
                            <OfferChatBubble>
                              <OfferChatMeta>
                                <span style={{ fontWeight: 600 }}>
                                  {(m.user_name || "Anonymous User").toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                                <span>·</span>
                                <span>
                                  {formatDistanceToNow(parseDate(m.created_at), {
                                    addSuffix: true,
                                  })}
                                </span>
                              </OfferChatMeta>
                              <OfferChatText>{m.text}</OfferChatText>
                            </OfferChatBubble>
                          </OfferChatMessage>
                        ))}
                    </OfferChatMessages>
                  ) : null}

                  {offerChatExpanded && (
                    <OfferChatInputForm>
                      <OfferChatTextarea
                        value={offerChatText}
                        onChange={(e) => setOfferChatText(e.target.value)}
                        placeholder="Send a message about your offer..."
                        disabled={isTaskCancelled}
                      />
                      <OfferChatActions>
                        <OfferChatButton
                          onClick={handleOfferChatSubmit}
                          disabled={!offerChatText.trim() || offerChatSubmitting || isTaskCancelled}
                        >
                          {offerChatSubmitting ? "Sending..." : "Send Message"}
                        </OfferChatButton>
                      </OfferChatActions>
                    </OfferChatInputForm>
                  )}
                </OfferChatSection>
              )}

              {/* Description */}
              <Section>
                <SectionTitle>
                  <Briefcase size={18} /> Project Description
                </SectionTitle>
                <Body>{linkifyText(task.description)}</Body>
              </Section>

              {/* Time Windows */}
              {task.time_windows?.length > 0 && (
                <Section>
                  <SectionTitle>
                    <Clock size={18} /> Availability
                  </SectionTitle>
                  <TagWrap>
                    {task.time_windows.map((tw) => (
                      <TimePill key={tw.id}>
                        {tw.name}: {tw.start_time} – {tw.end_time}
                      </TimePill>
                    ))}
                  </TagWrap>
                </Section>
              )}

              {/* Attachments */}
              {task.images?.length > 0 && (
                <Section>
                  <SectionTitle>
                    <ImageIcon size={18} /> Attachments
                  </SectionTitle>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {task.images.map((img) => (
                      <Thumb key={img.id} src={img.url} alt="Task Attachment" />
                    ))}
                  </div>
                </Section>
              )}

              <Activity
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                offers={offers}
                questions={questions}
                isOffersLoading={isOffersLoading}
                isQuestionsLoading={isQuestionsLoading}
                task={task}
                state={state}
                replyingTo={replyingTo}
                setReplyingTo={setReplyingTo}
                replyText={replyText}
                setReplyText={setReplyText}
                replySubmitting={replySubmitting}
                handleReplySubmit={handleReplySubmit}
                handleReport={handleReport}
                editingQuestion={editingQuestion}
                editQuestionText={editQuestionText}
                setEditQuestionText={setEditQuestionText}
                editSubmitting={editSubmitting}
                handleEditQuestion={handleEditQuestion}
                handleEditSubmit={handleEditSubmit}
                handleCancelEdit={handleCancelEdit}
                editingReply={editingReply}
                editReplyText={editReplyText}
                setEditReplyText={setEditReplyText}
                editReplySubmitting={editReplySubmitting}
                handleEditReply={handleEditReply}
                handleEditReplySubmit={handleEditReplySubmit}
                handleCancelEditReply={handleCancelEditReply}
                onEditOffer={() => {
                  setShowOfferForm(true);
                  setShowQuestionForm(false);
                  setTimeout(() => {
                    document.getElementById('offer-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                isPoster={isPoster}
                isTaskCancelled={isTaskCancelled}
                offerQuestionsById={offerQuestionsById}
                offerQuestionsLoadingById={offerQuestionsLoadingById}
                offerChatExpandedById={offerChatExpandedById}
                setOfferChatExpandedById={setOfferChatExpandedById}
                offerQuestionTextById={offerQuestionTextById}
                setOfferQuestionTextById={setOfferQuestionTextById}
                offerQuestionSubmittingById={offerQuestionSubmittingById}
                handleOfferQuestionSubmit={handleOfferQuestionSubmit}
              />
            </ContentArea>
          </Details>
          <MobileFooterSpacer />
        </Container>

        {/* Report Modal */}
        {isReportModalOpen && (
          <ReportModal onClick={() => setIsReportModalOpen(false)}>
            <ReportModalContent onClick={e => e.stopPropagation()}>
              <ReportModalTitle>Report Content</ReportModalTitle>
              
              <ReportTypeOption
                onClick={() => setSelectedReportType('spam')}
                style={{
                  background: selectedReportType === 'spam' ? '#f3f4f6' : '',
                  borderColor: selectedReportType === 'spam' ? '#9ca3af' : ''
                }}
              >
                <ReportOptionTitle>Spam</ReportOptionTitle>
                <ReportOptionDescription>Unwanted or repetitive content</ReportOptionDescription>
              </ReportTypeOption>

              <ReportTypeOption
                onClick={() => setSelectedReportType('fraud')}
                style={{
                  background: selectedReportType === 'fraud' ? '#f3f4f6' : '',
                  borderColor: selectedReportType === 'fraud' ? '#9ca3af' : ''
                }}
              >
                <ReportOptionTitle>Fraud/Scam</ReportOptionTitle>
                <ReportOptionDescription>Deceptive or fraudulent content</ReportOptionDescription>
              </ReportTypeOption>

              <ReportTypeOption
                onClick={() => setSelectedReportType('inappropriate')}
                style={{
                  background: selectedReportType === 'inappropriate' ? '#f3f4f6' : '',
                  borderColor: selectedReportType === 'inappropriate' ? '#9ca3af' : ''
                }}
              >
                <ReportOptionTitle>Inappropriate</ReportOptionTitle>
                <ReportOptionDescription>Offensive or inappropriate content</ReportOptionDescription>
              </ReportTypeOption>

              <ReportTypeOption
                onClick={() => setSelectedReportType('other')}
                style={{
                  background: selectedReportType === 'other' ? '#f3f4f6' : '',
                  borderColor: selectedReportType === 'other' ? '#9ca3af' : ''
                }}
              >
                <ReportOptionTitle>Other</ReportOptionTitle>
                <ReportOptionDescription>Other violation of community guidelines</ReportOptionDescription>
              </ReportTypeOption>

              <ReportModalButtons>
                <ReportModalButton 
                  className="cancel" 
                  onClick={() => setIsReportModalOpen(false)}
                >
                  Cancel
                </ReportModalButton>
                <ReportModalButton 
                  className="submit" 
                  onClick={submitReport}
                  disabled={!selectedReportType || isSubmittingReport}
                >
                  {isSubmittingReport ? 'Submitting...' : 'Submit Report'}
                </ReportModalButton>
              </ReportModalButtons>
            </ReportModalContent>
          </ReportModal>
        )}
      </Page>
    </>
  );
}

const mapStateToProps = (state) => ({ state });
export default connect(mapStateToProps)(TaskPage);
